<template>
  <div class="patient-form-container">
    <v-layout row wrap class="no-toppad" no-gutters text-xs-right>
    <v-container>
      <v-row noGutters>
        <v-col cols="12" md="12" sm="12">
          <div class="sections-container">
            <div class="info-section" v-if="patientList.length > 1">
              <h3 class="section-title">Select Patient</h3>
              <div class="patient-selector">
                <v-select
                  v-model="selectedPatient"
                  :items="patientList"
                  :item-text="item => `${item.firstName} ${item.lastName}`" 
                  item-value="item => item.id"
                  label="Patient"
                  return-object
                  class="line-input custom-filled"
                  dense
                  hide-details
                  @change="handlePatientSelect" />
              </div>
            </div>

            <div class="info-section">
              <v-progress-circular
                v-if="formLoading"
                indeterminate
                color="primary"
                class="loading-spinner" />

              <div v-if="!formLoading" class="forms-content">
                <v-stepper v-model="currentStep" class="modern-stepper">
                  <v-stepper-header class="stepper-header">
                    <template v-for="(form, index) in forms">
                      <v-stepper-step
                        :key="form.step"
                        :complete="currentStep > form.step"
                        :step="form.step"
                        class="step-item">
                        {{ form.name }}
                      </v-stepper-step>
                      <v-divider
                        v-if="index < forms.length - 1"
                        :key="`divider-${form.step}`"
                        class="step-divider" />
                    </template>
                  </v-stepper-header>

                  <v-stepper-items class="stepper-content">
                    <v-stepper-content
                      v-for="form in forms"
                      :key="`${form.step}-content`"
                      :step="form.step"
                      class="step-content">

                      <component
                        :is="form.component"
                        :patient="selectedPatient"
                        :userForm="form.userForm"
                        :formsDisabled="formsDisabled"
                        :needsUpdate="needsUpdate"
                        ref="currentFormComponent"
                        @goToNextForm="goToNextForm"
                        @submitFailure="submitFailure"
                        @setNeedsUpdate="setNeedsUpdate"
                        @reloadPatientForm="reloadPatientForm"
                        :key="reRenderKey" />

                      <div class="form-actions" v-if="!formsDisabled">
                        <v-btn
                          v-if="form.step != 1 && form.step != 2"
                          class="btn-secondary"
                          @click="previousStep(form.step)">
                          Previous
                        </v-btn>
                        <v-btn
                          v-if="form.step == 3"
                          class="btn-primary"
                          @click="nextStep(form.step)">
                          Update
                        </v-btn>
                        <v-btn
                          v-if="form.step != forms.length && form.step != 3"
                          class="btn-primary"
                          @click="nextStep(form.step)">
                          Next
                        </v-btn>
                        <v-btn
                          v-if="form.step == forms.length"
                          class="btn-primary"
                          @click="nextStep(form.step)">
                          Submit
                        </v-btn>
                      </div>
                    </v-stepper-content>
                  </v-stepper-items>
                </v-stepper>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-layout>
  </div>
</template>

<script>
import CustomForm from '@/components/Forms/CustomForm.vue';
import DentalForm from '@/components/Forms/DentalForm.vue';
import InsuranceForm from '@/components/Forms/InsuranceForm.vue'; 
import MedicalForm from '@/components/Forms/MedicalForm.vue';
import OfficePolicyForm from '@/components/Forms/OfficePolicyForm.vue';
import PrivacyPracticesForm from '@/components/Forms/PrivacyPracticesForm.vue';
import DataService from '../../_services/DataService';
import dateUtils from '@/../../backend/app/utils/dateUtils';

export default {
  name: 'PatientForms',
  components: {
    InsuranceForm,
    MedicalForm,
    DentalForm,
    OfficePolicyForm,
    PrivacyPracticesForm,
  },
  data() {
    return {
      currentStep: 1,
      formsDisabled: false,
      needsUpdate: false,
      forms: [
        {
          component: InsuranceForm,
          name: 'Insurance Form',
          step: 1,
        },
        {
          component: MedicalForm,
          name: 'Medical Form',
          step: 2,
        },
        {
          component: DentalForm,
          name: 'Dental Form',
          step: 3,
        },
        {
          component: OfficePolicyForm,
          name: 'Office Policy Form',
          step: 4,
        },
        {
          component: PrivacyPracticesForm,
          name: 'Privacy Practices Form',
          step: 5,
        },
      ],
      reRenderKey: 1,
      formLoading: true,
      patientList: [],
      selectedPatient: {},
      clientData: {},
    };
  },
  methods: {
    setStepIfBeyond(step) {
      if (this.currentStep > step) {
        this.currentStep = step;
      }
    },
    forceRerender() {
      this.reRenderKey += 1;
    },
    previousStep (n) {
      if (n !== 1) {
        this.currentStep = n - 1;
        window.scrollTo(0,0);
      }
    },
    async nextStep (n) {
      if (n <= this.forms.length) {
        // validate child form
        const formComp = Array.isArray(this.$refs.currentFormComponent)
          ? this.$refs.currentFormComponent[this.currentStep - 1]
          : this.$refs.currentFormComponent;

        if (formComp && typeof formComp.validate === 'function' && await formComp.validate()) {
          this.formLoading = true;
          // create pdf of current form
          await formComp.generatePDF();

        } else {
          // form not validated, create alert
          this.$root.$emit('raise_alert', { color: 'error', text: 'Please fill out the required inputs.' });
        }
      }
    },
    goToNextForm() {
      this.formLoading = false;
      this.currentStep++;
      window.scrollTo(0,0);
    },
    submitFailure() {
      this.formLoading = false;
    },
    setNeedsUpdate(value) {
      this.needsUpdate = value;
    },
    reloadPatientForm() {
      this.$router.go(0);
    },
    async getClientData() {
      const client = await DataService.getClientDetails();
      if (client) {
        this.clientData = client;
      }
    },
    async getFamily() {
      const response = await DataService.getPatientFamily();
      if (response.status === 200) {
        this.patientList = response.data;
        this.selectedPatient = this.patientList.find((pat) => pat.status === 'Self');
      }
    },
    async continueForms() {
      this.formLoading = true;
      this.formsDisabled = false;

      const latestFormResponse = await DataService.getLastPatientForm(this.selectedPatient.id);

      if (latestFormResponse.status === 200) {
        switch (latestFormResponse.data.Description) {
        case 'Registration - 1 - Insurance':
          this.currentStep = 2;
          break;
        case 'Registration - 2 - Medical History':
          this.currentStep = 3;
          break;
        case 'Registration - 3 - Dental Form':
          this.currentStep = 4;
          break;
        case 'Registration - 4 - Office Policy':
          this.currentStep = 5;
          break;
        case 'Registration - 5 - Privacy Notice':
          this.currentStep = 6;
          break;
        default:
          this.currentStep = 1;
          break;
        }
      }

      if (this.currentStep === 6) {
        if (!this.selectedPatient || !this.selectedPatient.id) {
          return; // Cannot proceed without patient ID
        }
        const latestMedicalFormResponse = await DataService.getLastPatientMedicalForm(this.selectedPatient.id);
        var updateNeeded = false;
        if (latestMedicalFormResponse.status === 200) {
          const latestMedicalFormDate = new Date(latestMedicalFormResponse.data.DateCreated);
          const now = new Date();
          const diffInDays = dateUtils.getDayDifference(latestMedicalFormDate, now);

          if (diffInDays >= this.clientData.intakeFormUpdateInterval) {
            updateNeeded = true;
          }
        }

        if (updateNeeded) {
          this.currentStep = 2;
        }else{
          const maybeId = this.selectedPatient.status === 'Self' ? undefined : this.selectedPatient.id;
          const formsResponse = await DataService.getFormsForPatient(maybeId);
          if (formsResponse.status === 200) {
            const pendingForms = formsResponse.data.filter((form) => !form.completedAt);
            if (pendingForms.length) {
              for (const form of pendingForms) {
                this.forms.push({
                  component: CustomForm,
                  name: form.name,
                  step: this.forms.length + 1,
                  userForm: form,
                });
              }

              // todo this is probably wrong.
              this.currentStep = 5;
              setTimeout(() => { this.currentStep = 6; }, 0);
            } else {
              this.formsDisabled = true;
            }
          } else {
            this.formsDisabled = true;
          }
        }
      }

      this.formLoading = false;
    },
    async handlePatientSelect() {
      await this.continueForms();
    },
  },
  async mounted(){
    await this.getClientData();
    await this.getFamily();
    await this.continueForms();
  },
};
</script>

<style lang="scss">
@import '@/scss/variables.scss';

.patient-form-container {
  // Component-specific colors
  $stepper-bg-color: #ffffff;
  $stepper-border-color: #e2e8f0;
  $step-inactive-bg: #e5e7eb;
  $step-inactive-text: #6b7280;
  $step-active-bg: #B0D35E;
  $step-active-text: #414a5b;
  $step-complete-bg: #10b981;
  $step-label-active: #1f2937;
  $divider-color: #d1d5db;
  $breakpoint-small: 480px;

  .sections-container {
    padding-bottom: 32px;
  }

  .patient-selector {
    max-width: 400px;
  }

  .forms-content {
    .modern-stepper {
      box-shadow: none !important;
      background: transparent !important;
      
      .stepper-header {
        box-shadow: none !important;
        background: $stepper-bg-color !important;
        border: 1px solid $stepper-border-color !important;
        border-radius: 12px !important;
        padding: 24px 16px 32px 16px !important;
        display: flex !important;
        align-items: flex-start !important;
        justify-content: space-between !important;
        flex-wrap: nowrap !important;
        height: 100%;
        
        @media (max-width: $breakpoint-tablet) {
          padding: 20px 12px 28px 12px !important;
        }
        
        @media (max-width: $breakpoint-mobile) {
          padding: 16px 8px 24px 8px !important;
          justify-content: space-around !important;
          flex-wrap: nowrap !important;
          
          .v-divider {
            display: none !important;
          }
        }
        
        @media (max-width: 350px) {
          display: grid !important;
          grid-template-columns: repeat(3, 1fr) !important;
          grid-template-rows: repeat(2, 1fr) !important;
          gap: 16px !important;
          justify-items: center !important;
          
          .v-stepper__step:nth-child(odd):not(.v-divider) {
            grid-column: span 1 !important;
          }
          
          .v-stepper__step:nth-child(10) { // Step 5
            grid-column: 2 !important;
            grid-row: 2 !important;
          }
          
          .v-divider {
            display: none !important;
          }
        }
        
        .v-stepper__step {
          flex-direction: column !important;
          align-items: center !important;
          text-align: center !important;
          padding: 0 !important;
          flex: 0 0 auto !important;
          min-width: 0 !important;
          justify-content: flex-start !important;
          align-self: flex-start !important;
          
          .v-stepper__step__step {
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
            background-color: $step-inactive-bg !important;
            color: $step-inactive-text !important;
            font-weight: 600 !important;
            font-size: 14px !important;
            margin-right: 0 !important;
            margin-bottom: 6px !important;
            border-radius: 50% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            transition: all 0.2s ease !important;
            line-height: 1 !important;
            text-align: center !important;
          }
          
          .v-stepper__label {
            font-size: 13px !important;
            font-weight: 500 !important;
            color: $step-inactive-text !important;
            text-transform: none !important;
            letter-spacing: 0 !important;
            line-height: 1.2 !important;
            white-space: normal !important;
            max-width: 120px !important;
            text-align: center !important;
            display: block !important;
            margin: 0 auto !important;
          }
          
          &.v-stepper__step--active {
            .v-stepper__step__step {
              background-color: $step-active-bg !important;
              color: $step-active-text !important;
              box-shadow: 0 0 0 3px rgba(176, 211, 94, 0.2) !important;
            }
            
            .v-stepper__label {
              color: $step-label-active !important;
              font-weight: 600 !important;
            }
          }
          
          &.v-stepper__step--complete {
            .v-stepper__step__step {
              background-color: $step-complete-bg !important;
              color: white !important;
              
              .v-icon {
                font-size: 1.15rem !important;
                color: white !important;
              }
            }
            
            .v-stepper__label {
              color: $step-label-active !important;
              font-weight: 500 !important;
            }
          }
        }
        
        @media (max-width: 1400px) {
          .v-stepper__step .v-stepper__label {
            display: none !important;
          }
        }
        
        .v-divider {
          border-color: $divider-color !important;
          margin: 0 !important;
          flex: 0 0 auto !important;
          width: 24px !important;
          height: 1px !important;
          
          @media (max-width: $breakpoint-mobile) {
            width: 16px !important;
          }
        }
      }
      
      .stepper-content {
        .step-content {
            padding: 24px 0;
        }
      }
    }
    
    .form-actions {
      display: flex;
      gap: 16px;
      justify-content: flex-end;
      padding-top: 24px;
      
      @media (max-width: 600px) {
        justify-content: center;
        flex-direction: column;
      }
    }
  }
}
</style>
